import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Gauge, AlertTriangle, Fuel, IndianRupee } from "lucide-react";
import { api, inr, fmtDate } from "@/lib/api";
import { PageHeader, StatCard, SearchBox, TableWrap, Empty, th, td, tr } from "@/components/common";

const STATES = ["All", "High fuel use", "Normal", "Odometer pending", "No fuel logged"];
const STATE_STYLE = {
  "High fuel use": "bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-950/40 dark:text-rose-400 dark:border-rose-800",
  Normal: "bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-400 dark:border-emerald-800",
  "Odometer pending": "bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950/40 dark:text-amber-400 dark:border-amber-800",
  "No fuel logged": "bg-slate-100 text-slate-600 border-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:border-slate-700",
};

export default function Mileage() {
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  const [q, setQ] = useState("");
  const [state, setState] = useState("All");
  useEffect(() => { api.get("/mileage").then((r) => setD(r.data)); }, []);
  const rows = useMemo(() => (d?.rows || []).filter((r) =>
    (state === "All" || r.state === state) &&
    `${r.lr_number} ${r.vehicle_number} ${r.pickup_location} ${r.drop_location}`.toLowerCase().includes(q.toLowerCase())
  ), [d, q, state]);
  if (!d) return <div className="text-sm text-muted-foreground">Loading…</div>;
  const kmpl = (v) => (v == null ? "—" : `${v.toFixed(2)} km/L`);

  return (
    <div>
      <PageHeader title="Route Mileage Check" subtitle="Fuel used per km for every trip · flagged trips burn >20% over expected" />
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4 md:gap-6">
        <StatCard testid="mileage-fleet-avg" label="Fleet Average" value={d.fleet_avg_kmpl ? `${d.fleet_avg_kmpl.toFixed(2)} km/L` : "—"} hint="Across delivered trips with fuel logs" icon={Gauge} />
        <StatCard testid="mileage-flagged" label="Flagged Trips" value={d.flagged} hint="Unusually high fuel use" icon={AlertTriangle} accent="text-rose-600" delay={50} />
        <StatCard testid="mileage-fuel-cost" label="Total Fuel Cost" value={inr(d.fuel_cost_month)} icon={IndianRupee} accent="text-amber-600" delay={100} />
        <StatCard testid="mileage-trips" label="Trips Tracked" value={d.rows.length} hint={`${d.rows.filter((r) => r.state === "Odometer pending").length} awaiting odometer`} icon={Fuel} delay={150} />
      </div>
      <div className="my-4 flex flex-col gap-3 sm:flex-row">
        <SearchBox testid="mileage-search-input" value={q} onChange={setQ} placeholder="Search LR, vehicle, route…" />
        <select data-testid="mileage-state-filter" className="native-select sm:w-48" value={state} onChange={(e) => setState(e.target.value)}>
          {STATES.map((s) => <option key={s}>{s}</option>)}
        </select>
      </div>
      <TableWrap>
        <thead><tr>{["LR", "Vehicle", "Route", "Distance", "Fuel (L)", "Fuel Cost", "km/L", "Expected", "₹/km", "Check"].map((h) => <th key={h} className={th}>{h}</th>)}</tr></thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id} className={`${tr} cursor-pointer`} onClick={() => navigate(`/bookings/${r.id}`)} data-testid={`mileage-row-${r.lr_number}`}>
              <td className={`${td} font-mono font-semibold text-primary`}>{r.lr_number}</td>
              <td className={`${td} font-mono text-xs`}>{r.vehicle_number}</td>
              <td className={td}>{r.pickup_location} → {r.drop_location}<div className="text-xs text-muted-foreground">{fmtDate(r.booking_date)} · {r.status === "In-Transit" ? "Delivery" : r.status === "Delivered" ? "Loading Point" : r.status}</div></td>
              <td className={`${td} amount`}>{r.distance_km != null ? `${r.distance_km.toLocaleString("en-IN")} km` : "—"}</td>
              <td className={`${td} amount`}>{r.litres || "—"}</td>
              <td className={`${td} amount`}>{r.fuel_cost ? inr(r.fuel_cost) : "—"}</td>
              <td className={`${td} amount font-semibold ${r.flagged ? "text-rose-600" : r.kmpl ? "text-emerald-600" : ""}`} data-testid={`mileage-kmpl-${r.lr_number}`}>{kmpl(r.kmpl)}</td>
              <td className={`${td} amount text-muted-foreground`}>{kmpl(r.expected_kmpl)}</td>
              <td className={`${td} amount`}>{r.cost_per_km != null ? `₹${r.cost_per_km}` : "—"}</td>
              <td className={td}><span className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border px-2.5 py-0.5 text-xs font-semibold ${STATE_STYLE[r.state]}`} data-testid={`mileage-flag-${r.lr_number}`}>{r.flagged && <AlertTriangle className="h-3 w-3" />}{r.state}</span></td>
            </tr>
          ))}
        </tbody>
      </TableWrap>
      {rows.length === 0 && <Empty testid="mileage-empty" text="No trips match. Trips appear here once a vehicle is assigned." />}
    </div>
  );
}
